(() => {
  const ready = (callback) => {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", callback);
    else callback();
  };
  const languageCodes = new Set(["en", "de", "fr", "nl", "it", "sl"]);
  const currentLanguage = (root, key) => languageCodes.has(root.dataset[key]) ? root.dataset[key] : "en";

  const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const cardIcon = (index) => ["◇", "✦", "◎", "●"][index] || "✦";
  const check = () => '<span class="hd-premium-check" aria-label="Yes">✓</span>';
  const miss = () => '<span class="hd-premium-miss" aria-label="No">×</span>';

  const sectionHead = (overline, title, body = "") => `
    <div class="hd-premium-section-head">
      <span>${escapeHtml(overline)}</span>
      <h2>${escapeHtml(title)}</h2>
      ${body ? `<p>${escapeHtml(body)}</p>` : ""}
    </div>
  `;

  const faqItems = (t) => [
    [t.faq1Q, t.faq1A],
    [t.faq2Q, t.faq2A],
    [t.faq3Q, t.faq3A],
    [t.faq4Q, t.faq4A],
    [t.faq5Q, t.faq5A],
    [t.faq6Q, t.faq6A],
    [t.faq7Q, t.faq7A],
    [t.faq8Q, t.faq8A],
  ];

  const renderJsonLd = (t, root) => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Humanodoro Pad",
      description: t.seoDesc,
      brand: { "@type": "Brand", name: "Humanodoro" },
    };
    if (root.dataset.price && root.dataset.currency) {
      schema.offers = {
        "@type": "Offer",
        price: root.dataset.price,
        priceCurrency: root.dataset.currency,
        availability: "https://schema.org/InStock",
      };
    }
    return `<script type="application/ld+json">${JSON.stringify(schema).replaceAll("<", "\\u003c")}<\/script>`;
  };

  const render = (t, root) => {
    const shopUrl = root.dataset.shopUrl || `${window.Shopify?.routes?.root || "/"}products/humanodoro-pad`;
    const comparePrice = root.dataset.comparePrice
      ? `<s>${escapeHtml(root.dataset.comparePrice)}</s>`
      : "";
    const gallery = [
      [root.dataset.sleepPad, t.galleryAlt1],
      [root.dataset.studyPad, t.galleryAlt2],
      [root.dataset.workPad, t.galleryAlt3],
    ];
    const why = [
      [t.why1Title, t.why1Text],
      [t.why2Title, t.why2Text],
      [t.why3Title, t.why3Text],
      [t.why4Title, t.why4Text],
    ];
    const steps = [
      ["01", t.how1Title, t.how1Text],
      ["02", t.how2Title, t.how2Text],
      ["03", t.how3Title, t.how3Text],
      ["04", t.how4Title, t.how4Text],
    ];
    const rows = [t.compRow1, t.compRow2, t.compRow3, t.compRow4, t.compRow5, t.compRow6];
    const moments = [
      [t.mom1Title, t.mom1Text, root.dataset.workPad],
      [t.mom2Title, t.mom2Text, root.dataset.studyPad],
      [t.mom3Title, t.mom3Text, root.dataset.sleepPerson],
      [t.mom4Title, t.mom4Text, root.dataset.bedsidePad],
    ];
    const testimonials = [
      [t.test1Text, t.test1Name, t.test1Role],
      [t.test2Text, t.test2Name, t.test2Role],
      [t.test3Text, t.test3Name, t.test3Role],
      [t.test4Text, t.test4Name, t.test4Role],
      [t.test5Text, t.test5Name, t.test5Role],
      [t.test6Text, t.test6Name, t.test6Role],
    ];
    const features = [t.optFeat1, t.optFeat2, t.optFeat3, t.optFeat4];

    return `
      ${renderJsonLd(t, root)}
      <div class="hd-premium-topbar">${escapeHtml(t.freeShipping)}</div>
      <header class="hd-premium-nav" data-hd-nav>
        <a class="hd-premium-brand" href="/">
          <img src="${root.dataset.logo}" width="32" height="39" alt="Humanodoro">
          <span>Humanodoro</span>
        </a>
        <nav>
          <a href="#how-it-works">${escapeHtml(t.howItWorksNav)}</a>
          <a href="#faq">${escapeHtml(t.faqNav)}</a>
          <a class="hd-premium-nav-cta" href="${escapeHtml(shopUrl)}">${escapeHtml(t.getHumanodoro)}</a>
        </nav>
      </header>

      <section class="hd-premium-hero">
        <div class="hd-premium-glow hd-premium-glow--one"></div>
        <div class="hd-premium-glow hd-premium-glow--two"></div>
        <div class="hd-container">
          <div class="hd-premium-hero-copy">
            <span class="hd-premium-badge">★★★★★ ${escapeHtml(t.heroBadge)}</span>
            <h1>${escapeHtml(t.heroTitle1)}<br><span>${escapeHtml(t.heroTitle2)}</span></h1>
            <p>${escapeHtml(t.heroSub)}</p>
            <div class="hd-premium-actions">
              <a class="hd-premium-btn hd-premium-btn--accent" href="${escapeHtml(shopUrl)}">${escapeHtml(t.heroCta)} <span aria-hidden="true">→</span></a>
              <a class="hd-premium-btn hd-premium-btn--ghost" href="#how-it-works">${escapeHtml(t.heroSecondary)}</a>
            </div>
          </div>
          <div class="hd-premium-video">
            <video autoplay loop muted playsinline>
              ${root.dataset.heroVideoWebm ? `<source src="${escapeHtml(root.dataset.heroVideoWebm)}" type="video/webm">` : ""}
              <source src="${escapeHtml(root.dataset.heroVideo)}" type="video/mp4">
            </video>
          </div>
        </div>
      </section>

      <section class="hd-premium-section">
        <div class="hd-container">
          ${sectionHead(t.impactOverline, t.impactTitle)}
          <div class="hd-premium-stats">
            <article><strong>${escapeHtml(t.stat1Value)}</strong><span>${escapeHtml(t.stat1Label)}</span></article>
            <article><strong>${escapeHtml(t.stat2Value)}</strong><span>${escapeHtml(t.stat2Label)}</span></article>
            <article><strong>${escapeHtml(t.stat3Value)}</strong><span>${escapeHtml(t.stat3Label)}</span></article>
          </div>
        </div>
      </section>

      <section class="hd-premium-section">
        <div class="hd-container">
          ${sectionHead(t.galleryOverline, t.galleryTitle)}
          <div class="hd-premium-gallery">
            ${gallery.map(([src, alt]) => `<img src="${src}" alt="${escapeHtml(alt)}" loading="lazy">`).join("")}
          </div>
        </div>
      </section>

      <section class="hd-premium-section hd-premium-section--alt">
        <div class="hd-container">
          ${sectionHead(t.whyOverline, t.whyTitle, t.whyBody)}
          <div class="hd-premium-card-grid">
            ${why.map(([title, text], index) => `
              <article class="hd-premium-card">
                <span>${cardIcon(index)}</span>
                <h3>${escapeHtml(title)}</h3>
                <p>${escapeHtml(text)}</p>
              </article>
            `).join("")}
          </div>
        </div>
      </section>

      <section id="how-it-works" class="hd-premium-section">
        <div class="hd-container">
          ${sectionHead(t.howOverline, t.howTitle)}
          <div class="hd-premium-steps">
            ${steps.map(([num, title, text]) => `
              <article>
                <strong>${escapeHtml(num)}</strong>
                <h3>${escapeHtml(title)}</h3>
                <p>${escapeHtml(text)}</p>
              </article>
            `).join("")}
          </div>
        </div>
      </section>

      <section class="hd-premium-section hd-premium-section--alt">
        <div class="hd-container">
          ${sectionHead(t.compOverline, t.compTitle, t.compBody)}
          <div class="hd-premium-table">
            <div class="hd-premium-table-head"><span></span><strong>${escapeHtml(t.compCol1)}</strong><strong>${escapeHtml(t.compCol2)}</strong><strong>${escapeHtml(t.compCol3)}</strong></div>
            ${rows.map((row) => `<div class="hd-premium-table-row"><span>${escapeHtml(row)}</span><b>${check()}</b><b>${miss()}</b><b>${miss()}</b></div>`).join("")}
          </div>
        </div>
      </section>

      <section class="hd-premium-section">
        <div class="hd-container">
          ${sectionHead(t.momOverline, t.momTitle)}
          <div class="hd-premium-moments">
            ${moments.map(([title, text, image]) => `
              <article class="hd-premium-moment">
                <img src="${image}" alt="${escapeHtml(title)}" loading="lazy">
                <div><h3>${escapeHtml(title)}</h3><p>${escapeHtml(text)}</p></div>
              </article>
            `).join("")}
          </div>
        </div>
      </section>

      <section class="hd-premium-section hd-premium-section--alt">
        <div class="hd-container">
          ${sectionHead(t.testOverline, t.testTitle)}
          <div class="hd-premium-testimonials">
            ${testimonials.map(([text, name, role]) => `
              <article class="hd-premium-card">
                <span>★★★★★</span>
                <p>“${escapeHtml(text)}”</p>
                <small>${escapeHtml(name)} · ${escapeHtml(role)}</small>
              </article>
            `).join("")}
          </div>
        </div>
      </section>

      <section class="hd-premium-section">
        <div class="hd-container">
          ${sectionHead(t.optOverline, t.optTitle)}
          <article class="hd-premium-offer">
            <span>${escapeHtml(t.optBadge)}</span>
            <h3>${escapeHtml(t.optCardTitle)}</h3>
            <p>${escapeHtml(t.optCardDesc)}</p>
            <div><strong>${escapeHtml(root.dataset.displayPrice || "")}</strong>${comparePrice}</div>
            <em>${escapeHtml(t.optSave)}</em>
            <small>${escapeHtml(t.optShipping)}</small>
            <ul>${features.map((item) => `<li>${check()} ${escapeHtml(item)}</li>`).join("")}</ul>
            <a class="hd-premium-btn hd-premium-btn--accent" href="${escapeHtml(shopUrl)}">${escapeHtml(t.optCta)} <span aria-hidden="true">→</span></a>
          </article>
        </div>
      </section>

      <section id="faq" class="hd-premium-section hd-premium-section--alt">
        <div class="hd-container hd-premium-faq">
          ${sectionHead(t.faqOverline, t.faqTitle)}
          ${faqItems(t).map(([q, a]) => `<details><summary>${escapeHtml(q)}</summary><p>${escapeHtml(a)}</p></details>`).join("")}
        </div>
      </section>

      <section class="hd-premium-final">
        <div class="hd-container">
          <h2>${escapeHtml(t.ctaTitle1)}<br><span>${escapeHtml(t.ctaTitle2)}</span></h2>
          <p>${escapeHtml(t.ctaBody)}</p>
          <a class="hd-premium-btn hd-premium-btn--accent" href="${escapeHtml(shopUrl)}">${escapeHtml(t.ctaCta)} <span aria-hidden="true">→</span></a>
        </div>
      </section>

      <div class="hd-premium-sticky">
        <a class="hd-premium-btn hd-premium-btn--accent" href="${escapeHtml(shopUrl)}">${escapeHtml(t.stickyCta)} <span aria-hidden="true">→</span></a>
      </div>
    `;
  };

  ready(() => {
    document.querySelectorAll("[data-hd-premium-root]").forEach((root) => {
      const lang = currentLanguage(root, "premiumLang");
      const t = window.HD_PREMIUM_COPY?.[lang] || window.HD_PREMIUM_COPY?.en;
      if (!t) return;
      root.innerHTML = render(t, root);
      root.hidden = false;
      document.title = `${t.seoTitle} – Humanodoro`;
      const description = document.querySelector("meta[name='description']");
      if (description) description.setAttribute("content", t.seoDesc);
    });
  });
})();
